import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContracts';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import AdminSection from './AdminSection';
import ButtonLoader from '../common/ButtonLoader';

const Block = ({
  label, owner, admin,
  onTransfer, onUpdateAdmin,
  onUpdateMissing, onUpdateDeduction
}) => {
  const [busy, setBusy] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [newAdmin, setNewAdmin] = useState('');
  const [newMissing, setNewMissing] = useState('');
  const [newDeduction, setNewDeduction] = useState('');

  const safe = async (key, fn, value) => {
    if (!ethers.utils.isAddress(value)) return;
    setBusy(key);
    try { await fn(value); }
    finally { setBusy(''); }
  };

  return (
    <div className="admin-tile">
      <div className="admin-tile-title">{label}</div>

      <div className="admin-tile-meta">Owner: <code>{owner || '—'}</code></div>
      <div className="admin-tile-meta">Admin: <code>{admin || '—'}</code></div>

      <div className="admin-form" style={{ marginTop: 10 }}>
        <div className="admin-form-row">
          <label>New Owner</label>
          <input value={newOwner} onChange={(e) => setNewOwner(e.target.value)} placeholder="0x…" />
        </div>
        <button
          className="btn btn-ghost"
          disabled={busy === 'own' || !newOwner}
          onClick={() => safe('own', onTransfer, newOwner)}
        >
          {busy === 'own' ? <ButtonLoader label="Transferring" /> : 'Transfer Ownership'}
        </button>

        <div className="admin-form-row" style={{ marginTop: 10 }}>
          <label>New Admin</label>
          <input value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} placeholder="0x…" />
        </div>
        <button
          className="btn btn-ghost"
          disabled={busy === 'adm' || !newAdmin}
          onClick={() => safe('adm', onUpdateAdmin, newAdmin)}
        >
          {busy === 'adm' ? <ButtonLoader label="Updating" /> : 'Update Admin'}
        </button>

        {onUpdateMissing && (
          <>
            <div className="admin-form-row" style={{ marginTop: 10 }}>
              <label>New Missing Wallet</label>
              <input value={newMissing} onChange={(e) => setNewMissing(e.target.value)} placeholder="0x…" />
            </div>
            <button
              className="btn btn-ghost"
              disabled={busy === 'mis' || !newMissing}
              onClick={() => safe('mis', onUpdateMissing, newMissing)}
            >
              {busy === 'mis' ? <ButtonLoader label="Updating" /> : 'Update Missing Wallet'}
            </button>
          </>
        )}

        {onUpdateDeduction && (
          <>
            <div className="admin-form-row" style={{ marginTop: 10 }}>
              <label>New Deduction Wallet</label>
              <input value={newDeduction} onChange={(e) => setNewDeduction(e.target.value)} placeholder="0x…" />
            </div>
            <button
              className="btn btn-ghost"
              disabled={busy === 'ded' || !newDeduction}
              onClick={() => safe('ded', onUpdateDeduction, newDeduction)}
            >
              {busy === 'ded' ? <ButtonLoader label="Updating" /> : 'Update Deduction Wallet'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const AdminOwnership = ({ role, details, onChanged }) => {
  const { plans, coreStorage, booster } = useContracts();
  const notify = useNotify();

  const wrap = (fn, label) => async (val) => {
    try {
      const tx = await fn(val);
      notify.info('Submitting', `${label} sent…`);
      await tx.wait();
      notify.success('Done', label);
      onChanged && onChanged();
    } catch (e) {
      notify.error('Failed', parseError(e));
    }
  };

  return (
    <AdminSection
      title="Ownership & Roles"
      subtitle="Transfer contract ownership, rotate admin, change missing wallet. Use with caution — irreversible."
      ownerOnly
      role={role}
    >
      <div className="admin-grid-3">
        <Block
          label="Plans"
          owner={details?.plans?.owner}
          admin={details?.plans?.admin}
          onTransfer={wrap((v) => plans.write.transferOwnership(v), 'Plans ownership transfer')}
          onUpdateAdmin={wrap((v) => plans.write.updateAdmin(v), 'Plans admin update')}
        />
        <Block
          label="CoreStorage"
          owner={details?.coreStorage?.owner}
          admin={details?.coreStorage?.admin}
          onTransfer={wrap((v) => coreStorage.write.transferOwnership(v), 'CoreStorage ownership transfer')}
          onUpdateAdmin={wrap((v) => coreStorage.write.updateAdmin(v), 'CoreStorage admin update')}
          onUpdateMissing={wrap((v) => coreStorage.write.updateMissingWallet(v), 'Missing wallet update')}
          onUpdateDeduction={wrap((v) => coreStorage.write.updateDeductionWallet(v), 'Deduction wallet update')}
        />
        <Block
          label="Booster"
          owner={details?.booster?.owner}
          admin={details?.booster?.admin}
          onTransfer={wrap((v) => booster.write.transferOwnership(v), 'Booster ownership transfer')}
          onUpdateAdmin={wrap((v) => booster.write.updateAdmin(v), 'Booster admin update')}
        />
      </div>
    </AdminSection>
  );
};

export default AdminOwnership;
